// ═══════════════════════════════════════════════════════════════
// VOLUNTEEROS — Schedule
// All assignments in a schedule view with Upcoming/Past tabs,
// grouping by shiftDate, with filters and status actions
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Select,
  Loader, Center, ActionIcon, Tooltip,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconCalendar, IconCheck, IconX, IconBan,
  IconFilter,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useVolunteerStore } from '../../store/volunteerStore';
import { useAuthStore } from '../../store/authStore';
import type { VolAssignment } from '../../store/volunteerStore';

// ── Status badge config ──────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'var(--color-primary-700)',
  confirmed: 'var(--color-success)',
  completed: '#03AC9E',
  'no-show': 'var(--color-error)',
  cancelled: '#939598',
  declined: '#F86A0B',
};

export default function VolunteerSchedule() {
  const {
    volunteers, events, assignments,
    loadVolunteers, loadEvents, loadAssignments, updateAssignment,
  } = useVolunteerStore();
  const { activeIncident } = useAuthStore();

  const [initialLoad, setInitialLoad] = useState(true);
  const [filterVolunteer, setFilterVolunteer] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [filterEvent, setFilterEvent] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    await Promise.all([loadVolunteers(), loadEvents(), loadAssignments()]);
  }, [loadVolunteers, loadEvents, loadAssignments]);

  useEffect(() => {
    fetchData().finally(() => setInitialLoad(false));
  }, [fetchData]);

  // ── Lookups ────────────────────────────────────────────────

  const volunteerName = useCallback((volId: string) => {
    const v = volunteers.find(vol => String(vol.id) === String(volId));
    return v ? `${v.firstName} ${v.lastName}`.trim() || 'Unknown' : 'Unknown';
  }, [volunteers]);

  const eventName = useCallback((evId: string) => {
    const e = events.find(ev => String(ev.id) === String(evId));
    return e?.name || e?.eventName || `Event #${evId}`;
  }, [events]);

  // ── Filter options ─────────────────────────────────────────

  const volunteerOptions = useMemo(() =>
    volunteers.map(v => ({
      value: String(v.id),
      label: `${v.firstName} ${v.lastName}`.trim() || `#${v.id}`,
    })),
    [volunteers],
  );

  const eventOptions = useMemo(() =>
    events.map(e => ({
      value: String(e.id),
      label: e.eventName || `#${e.id}`,
    })),
    [events],
  );

  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'no-show', label: 'No-Show' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'declined', label: 'Declined' },
  ];

  // ── Filtered + split assignments ───────────────────────────

  const now = dayjs();

  const filtered = useMemo(() => {
    let list = [...assignments];
    if (filterVolunteer) list = list.filter(a => String(a.volunteerId) === filterVolunteer);
    if (filterEvent) list = list.filter(a => String(a.eventId) === filterEvent);
    if (filterStatus) list = list.filter(a => a.status === filterStatus);
    if (activeIncident) list = list.filter(a => String(a.incidentId) === String(activeIncident.id));
    return list;
  }, [assignments, filterVolunteer, filterEvent, filterStatus, activeIncident]);

  const { upcoming, past } = useMemo(() => {
    const up: VolAssignment[] = [];
    const pa: VolAssignment[] = [];
    filtered.forEach(a => {
      const d = a.shiftDate ? dayjs(a.shiftDate) : null;
      if (d && d.isBefore(now, 'day')) {
        pa.push(a);
      } else {
        up.push(a);
      }
    });
    up.sort((a, b) => (a.shiftDate ?? '').localeCompare(b.shiftDate ?? ''));
    pa.sort((a, b) => (b.shiftDate ?? '').localeCompare(a.shiftDate ?? ''));
    return { upcoming: up, past: pa };
  }, [filtered, now]);

  // Group assignments by shiftDate
  const groupByDate = (list: VolAssignment[]): Array<{ date: string; items: VolAssignment[] }> => {
    const map = new Map<string, VolAssignment[]>();
    for (const a of list) {
      const key = a.shiftDate || 'No Date';
      const arr = map.get(key);
      if (arr) arr.push(a);
      else map.set(key, [a]);
    }
    return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
  };

  // ── Status actions ─────────────────────────────────────────

  const handleStatusChange = async (id: number, newStatus: string) => {
    setUpdatingId(id);
    try {
      await updateAssignment(id, { status: newStatus });
      notifications.show({
        title: 'Assignment updated',
        message: `Status changed to ${newStatus}`,
        color: STATUS_COLORS[newStatus] ?? 'var(--color-primary-700)',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update assignment status',
        color: 'var(--color-error)',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  // Determine which action buttons to show based on current status
  const renderActions = (a: VolAssignment) => {
    const loading = updatingId === a.id;
    const btns: React.ReactNode[] = [];

    if (a.status === 'scheduled') {
      btns.push(
        <Tooltip label="Confirm" key="confirm">
          <ActionIcon size="sm" variant="light" color="var(--color-success)" loading={loading}
            onClick={() => handleStatusChange(a.id, 'confirmed')}>
            <IconCheck size={14} />
          </ActionIcon>
        </Tooltip>,
        <Tooltip label="Decline" key="decline">
          <ActionIcon size="sm" variant="light" color="#F86A0B" loading={loading}
            onClick={() => handleStatusChange(a.id, 'declined')}>
            <IconX size={14} />
          </ActionIcon>
        </Tooltip>,
        <Tooltip label="Cancel" key="cancel">
          <ActionIcon size="sm" variant="light" color="#939598" loading={loading}
            onClick={() => handleStatusChange(a.id, 'cancelled')}>
            <IconBan size={14} />
          </ActionIcon>
        </Tooltip>,
      );
    } else if (a.status === 'confirmed') {
      btns.push(
        <Tooltip label="Mark Completed" key="complete">
          <ActionIcon size="sm" variant="light" color="#03AC9E" loading={loading}
            onClick={() => handleStatusChange(a.id, 'completed')}>
            <IconCheck size={14} />
          </ActionIcon>
        </Tooltip>,
        <Tooltip label="Cancel" key="cancel">
          <ActionIcon size="sm" variant="light" color="#939598" loading={loading}
            onClick={() => handleStatusChange(a.id, 'cancelled')}>
            <IconBan size={14} />
          </ActionIcon>
        </Tooltip>,
      );
    }

    return btns.length > 0 ? <Group gap={4}>{btns}</Group> : <Text size="xs" c="dimmed">—</Text>;
  };

  // ── Render a date-grouped section ──────────────────────────

  const renderGroup = (list: VolAssignment[]) => {
    const groups = groupByDate(list);
    if (groups.length === 0) {
      return (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          No assignments to display.
        </Text>
      );
    }

    return (
      <Stack gap="md">
        {groups.map(g => (
          <Paper key={g.date} p="md" radius="sm" withBorder>
            <Group gap="xs" mb="sm">
              <IconCalendar size={14} />
              <Text fw={600} size="sm">
                {g.date !== 'No Date' ? dayjs(g.date).format('ddd, MMM D YYYY') : 'No Date'}
              </Text>
              <Badge size="xs" variant="light" color="#939598">{g.items.length}</Badge>
            </Group>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--frost)' }}>
                    {['Volunteer','Event','Role','Time','Status','Actions'].map(h => (
                      <th key={h} style={{ padding: '8px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--slate)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.items.map(a => (
                    <tr key={a.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '8px 8px' }}>{volunteerName(a.volunteerId)}</td>
                      <td style={{ padding: '8px 8px' }}>{eventName(a.eventId)}</td>
                      <td style={{ padding: '8px 8px' }}>{a.role || '—'}</td>
                      <td style={{ padding: '8px 8px', whiteSpace: 'nowrap' }}>
                        {a.startTime || a.endTime ? `${a.startTime || '?'} – ${a.endTime || '?'}` : '—'}
                      </td>
                      <td style={{ padding: '8px 8px' }}>
                        <Badge size="xs" color={STATUS_COLORS[a.status] ?? '#939598'} tt="capitalize">
                          {a.status}
                        </Badge>
                      </td>
                      <td style={{ padding: '8px 8px' }}>{renderActions(a)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Paper>
        ))}
      </Stack>
    );
  };

  // ── Main render ────────────────────────────────────────────

  if (initialLoad) {
    return <Center h={400}><Loader color="var(--color-primary-700)" /></Center>;
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">Schedule</Title>
          <Text size="sm" c="dimmed">
            {filtered.length} assignment{filtered.length !== 1 ? 's' : ''}{' '}
            {activeIncident ? `for ${activeIncident.name}` : 'across all incidents'}
          </Text>
        </div>
        <Group gap="xs" align="flex-end">
          <IconFilter size={14} style={{ color: 'var(--slate)', marginBottom: 8 }} />
          <Select
            placeholder="Volunteer"
            data={volunteerOptions}
            value={filterVolunteer}
            onChange={setFilterVolunteer}
            clearable
            searchable
            size="xs"
            w={160}
          />
          <Select
            placeholder="Event"
            data={eventOptions}
            value={filterEvent}
            onChange={setFilterEvent}
            clearable
            searchable
            size="xs"
            w={160}
          />
          <Select
            placeholder="Status"
            data={statusOptions}
            value={filterStatus}
            onChange={setFilterStatus}
            clearable
            size="xs"
            w={130}
          />
        </Group>
      </Group>

      <Group gap="xs" mb="sm">
        <Select
          placeholder="Time"
          data={[
            { value: 'all', label: `All (${filtered.length})` },
            { value: 'upcoming', label: `Upcoming (${upcoming.length})` },
            { value: 'past', label: `Past (${past.length})` },
          ]}
          value={timeFilter}
          onChange={v => setTimeFilter(v ?? 'all')}
          size="xs"
          w={160}
        />
      </Group>

      {renderGroup(timeFilter === 'past' ? past : timeFilter === 'upcoming' ? upcoming : [...upcoming, ...past])}
    </div>
  );
}
